// A full attack's parts in the log (qa-runs#1158; docs/VTT/design/29-the-attack-chain.md §2.9): which swing a line is,
// the label on a swing the server set aside because its target was down, the leftover offer ("Thorga's target is down.
// 2 swings left: send them at…"), and the order a DM settles the swings' damage in. The server decides what is set aside
// and checks every swing sent on; the browser only offers what this person's own store holds.
import { useEffect, useSyncExternalStore, useState } from 'react';
import {
  attackOutcome, damageOf, isDown, isLeftover, relation, squaresFor, tokenLabel, squares3D, heightSquares, elevation, type Floor,
  type AttackSpec, type Diagonal, type PlayerAttack, type Relation, type RollRecord,
} from '@dragons-table/vtt-shared';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';

const btn = BTN;

// ── What this browser remembers (per campaign, in local storage) ─────────────────────────────────────────────────────
// The swings it sent for each full attack, so a leftover swing goes on with exactly the spec it was made with (the
// server's record has the situation's terms folded in); and the leftover swings sent on or let go, so the offer goes.

type Gone = 'sent' | 'let go';
interface Sent { token: string; swings: AttackSpec[]; group?: string }
interface Memory { gone: Record<string, Gone>; sent: Sent[] }
const EMPTY: Memory = { gone: {}, sent: [] };
const SENT_KEEP = 20;
const GONE_KEEP = 300;
const keyOf = (campaignId: string) => `vtt.fullAttack.${campaignId}`;
const cache = new Map<string, { raw: string | null; memory: Memory }>();
const listeners = new Set<() => void>();
let storageBroken = false;

function parseMemory(raw: string | null): Memory {
  if (!raw) return EMPTY;
  try {
    const v = JSON.parse(raw) as Partial<Memory>;
    const gone = v && typeof v.gone === 'object' && v.gone !== null ? v.gone : {};
    const sent = Array.isArray(v?.sent) ? v.sent.filter((s) => s && typeof s.token === 'string' && Array.isArray(s.swings)) : [];
    return { gone, sent };
  } catch { return EMPTY; }
}

function readMemory(campaignId: string): Memory {
  const have = cache.get(campaignId);
  if (storageBroken) return have?.memory ?? EMPTY;
  let raw: string | null;
  try { raw = localStorage.getItem(keyOf(campaignId)); } catch { storageBroken = true; return have?.memory ?? EMPTY; }
  if (have && have.raw === raw) return have.memory;
  const memory = parseMemory(raw);
  cache.set(campaignId, { raw, memory });
  return memory;
}

function writeMemory(campaignId: string, memory: Memory): void {
  const raw = JSON.stringify(memory);
  try { localStorage.setItem(keyOf(campaignId), raw); } catch { storageBroken = true; }
  cache.set(campaignId, { raw, memory });
  for (const l of listeners) l();
}

const subscribe = (l: () => void) => { listeners.add(l); return () => { listeners.delete(l); }; };
function useMemory(campaignId: string): Memory {
  return useSyncExternalStore(subscribe, () => readMemory(campaignId));
}

/** A full attack this browser sent: its token and every swing, in order (the extra swing at its place). */
export function rememberFullAttack(campaignId: string, token: string, swings: AttackSpec[]): void {
  const m = readMemory(campaignId);
  writeMemory(campaignId, { ...m, sent: [...m.sent, { token, swings }].slice(-SENT_KEEP) });
}

function markGone(campaignId: string, ids: string[], how: Gone): void {
  const m = readMemory(campaignId);
  const gone = { ...m.gone };
  for (const id of ids) gone[id] = how;
  const keys = Object.keys(gone);
  for (const k of keys.slice(0, Math.max(0, keys.length - GONE_KEEP))) delete gone[k];
  writeMemory(campaignId, { ...m, gone });
}

/**
 * The swings a full attack was sent with, by its group: the one this browser already matched to it, else the newest it
 * sent with that token and that many swings and has not matched yet (the server names the group; the browser never
 * learns it at sending). Null: this browser did not send it. Pure: `bindSwings` keeps the match.
 */
function findSwings(m: Memory, group: string, token: string, of: number): { swings: AttackSpec[]; index: number; bound: boolean } | null {
  const b = m.sent.findIndex((s) => s.group === group);
  if (b >= 0) return { swings: m.sent[b]!.swings, index: b, bound: true };
  let i = m.sent.length - 1;
  while (i >= 0 && !(m.sent[i]!.group === undefined && m.sent[i]!.token === token && m.sent[i]!.swings.length === of)) i -= 1;
  return i < 0 ? null : { swings: m.sent[i]!.swings, index: i, bound: false };
}

function bindSwings(campaignId: string, index: number, group: string): void {
  const m = readMemory(campaignId);
  if (!m.sent[index] || m.sent[index]!.group !== undefined) return;
  writeMemory(campaignId, { ...m, sent: m.sent.map((s, k) => (k === index ? { ...s, group } : s)) });
}

// ── The swings in the log ────────────────────────────────────────────────────────────────────────────────────────────

/** A set-aside swing as either log holds it: the DM's record, or a player's own line. */
interface Leftover { id: string; group: string; swing: number; of: number; token: string; ranged: boolean; attacker: string | null; priv: boolean }

type Entry = ReturnType<typeof useTableState>['log'][number];

/** The set-aside swings in the log not yet sent on (the DM's log shows a sent-on swing by its `full.from`), in log order. */
function leftoversIn(log: Entry[]): Leftover[] {
  const sentOn = new Set<string>();
  for (const e of log) if (e.kind === 'roll' && e.roll.attack?.full?.from) sentOn.add(e.roll.attack.full.from);
  const out: Leftover[] = [];
  for (const e of log) {
    if (e.kind === 'roll' && e.roll.attack && isLeftover(e.roll.attack) && !sentOn.has(e.roll.id)) {
      const f = e.roll.attack;
      out.push({ id: e.roll.id, group: f.full!.group, swing: f.full!.swing, of: f.full!.of, token: f.attacker.id, ranged: !!f.ranged, attacker: f.attacker.name, priv: e.roll.private });
    } else if (e.kind === 'attack' && e.attack.leftover && e.attack.full) {
      const a = e.attack;
      out.push({ id: a.id, group: a.full!.group, swing: a.full!.swing, of: a.full!.of, token: a.full!.token, ranged: !!a.full!.ranged, attacker: a.attacker?.name ?? null, priv: false });
    }
  }
  return out;
}

/**
 * Which earlier swing of the same full attack a DM's damage offer waits on (qa-runs#1158, "swings settle in order"):
 * the first one before it still waiting for a defence or for its damage to be applied or dropped, by its number (1 up);
 * null when none does. So the table knows whether the target fell before the next hit lands.
 */
export function waitsOnSwing(roll: RollRecord, log: Entry[]): number | null {
  const f = roll.attack?.full;
  if (!f) return null;
  for (const e of log) {
    if (e.kind !== 'roll' || e.roll.id === roll.id) continue;
    const g = e.roll.attack;
    if (!g?.full || g.full.group !== f.group || g.full.swing >= f.swing || g.settled) continue;
    if (g.ac === null || damageOf(g, attackOutcome(e.roll, g))) return g.full.swing + 1;
  }
  return null;
}

/** How many squares apart two tokens' spaces are, edge to edge (0: touching), by the diagonal rule. */
function gapSquares(a: Placed, b: Placed, pitch: { x: number; y: number }, diagonal: Diagonal, dz = 0): number {
  const gx = Math.max(0, Math.round((Math.abs(a.x - b.x) - ((a.size.w + b.size.w) / 2) * pitch.x) / pitch.x));
  const gy = Math.max(0, Math.round((Math.abs(a.y - b.y) - ((a.size.h + b.size.h) / 2) * pitch.y) / pitch.y));
  return dz ? squares3D(gx, gy, dz, diagonal) : squaresFor(gx, gy, diagonal);
}
interface Placed { x: number; y: number; size: { w: number; h: number }; floor?: string; z?: number }

/**
 * Whether `other` is within a melee reach of `attacker` (qa-runs#1158): its natural reach, one square for a creature
 * of one square or smaller, else as many squares as it is wide (a Large creature's 10 ft). A reach weapon is not known
 * to the table, so the DM, who may send a swing anywhere, covers it.
 */
export function withinReach(attacker: Placed, other: Placed, pitch: { x: number; y: number }, diagonal: Diagonal, scene?: { floors?: Floor[] }): boolean {
  const reach = Math.max(1, Math.floor(attacker.size.w));
  // Counting the height between them (qa-runs#1160): a fighter on the ground does not reach a griffon 40 ft up.
  const dz = scene ? heightSquares(elevation(other, scene) - elevation(attacker, scene), diagonal) : 0;
  return gapSquares(attacker, other, pitch, diagonal, dz) + 1 <= reach;
}

interface TokenLike extends Placed {
  id: string; kind: string; name?: string; pc: boolean; owners: string[]; disposition?: 'friend' | 'neutral' | 'foe';
  dead?: boolean; values?: { hp?: { current: number } };
}
const RANK: Record<Relation, number> = { enemy: 0, neutral: 1, ally: 2 };

/**
 * The label on a full attack's swing (both logs) and, on the first of a full attack's swings still set aside, the offer
 * to send them on: at a creature this person can see, enemies first, a melee swing only within reach (the DM: anyone),
 * one `attack.request { leftover }` a swing, in order; or let them go, remembered here, nothing sent.
 */
export function FullSwingParts({ id, full, leftover, fromSent }: { id: string; full: { group: string; swing: number; of: number }; leftover: boolean; /** Sent on from a set-aside swing (the DM's record). */ fromSent?: boolean }) {
  const { log } = useTableState();
  const { campaignId } = useShell();
  const memory = useMemory(campaignId);
  const all = leftoversIn(log);
  const sentOn = leftover && !all.some((l) => l.id === id);
  const gone = memory.gone[id];
  const pending = all.filter((l) => l.group === full.group && !memory.gone[l.id]).sort((a, b) => a.swing - b.swing);
  const offerHere = leftover && !sentOn && !gone && all.find((l) => l.group === full.group && !memory.gone[l.id])?.id === id;
  return (
    <>
      <div className="text-[0.625rem] text-stone-400" data-testid={`full-swing-${id}`}>
        Full attack: swing {full.swing + 1} of {full.of}{fromSent ? ', sent on from a swing set aside' : ''}
        {leftover && (
          <span className="ml-1 text-amber-400" data-testid={`set-aside-${id}`}>
            · Set aside: its target was down{sentOn || gone === 'sent' ? ' (sent on)' : gone === 'let go' ? ' (let go)' : ''}
          </span>
        )}
      </div>
      {offerHere && pending.length > 0 && <LeftoverOffer pending={pending} />}
    </>
  );
}

function LeftoverOffer({ pending }: { pending: Leftover[] }) {
  const { store, act, setNotice, role, systemRules } = useTableState();
  const { campaignId } = useShell();
  const [target, setTarget] = useState('');
  const [sending, setSending] = useState(false);
  const isDm = role === 'dm';
  const first = pending[0]!;
  const objects = store.state.objects as Record<string, TokenLike>;
  const encounter = store.state.encounter as Parameters<typeof tokenLabel>[1];
  const me = objects[first.token];
  const nameOf = (o: TokenLike) => tokenLabel(o, encounter) || 'Unnamed token';
  const attackerName = (me ? tokenLabel(me, encounter) : null) ?? first.attacker ?? 'Your attacker';
  const grid = store.state.grid as { x: { basePitch: number }; y: { basePitch: number } } | undefined;
  const pitch = { x: grid?.x.basePitch ?? 70, y: grid?.y.basePitch ?? 70 };
  const diagonal: Diagonal = systemRules.distance?.diagonal ?? 'one';
  // A melee swing goes at a creature within reach of a player's token; a ranged one at anything they can see. The
  // player's store is their own projection: what they cannot see is not in it. The DM may send them anywhere.
  const melee = pending.some((l) => !l.ranged);
  const candidates = Object.values(objects)
    .filter((o) => o.kind === 'token' && o.id !== first.token && !isDown(o))
    .filter((o) => isDm || !melee || !me || withinReach(me, o, pitch, diagonal, store.state.scene))
    .map((o) => ({ o, rel: me ? relation({ id: me.id, pc: me.pc, owners: me.owners ?? [], ...(me.disposition ? { disposition: me.disposition } : {}) }, { id: o.id, pc: o.pc, owners: o.owners ?? [], ...(o.disposition ? { disposition: o.disposition } : {}) }) : 'neutral' as Relation }))
    .sort((a, b) => RANK[a.rel] - RANK[b.rel] || nameOf(a.o).localeCompare(nameOf(b.o)));
  const n = pending.length;
  const memory = useMemory(campaignId);
  const found = findSwings(memory, first.group, first.token, first.of);
  const swings = found?.swings ?? null;
  useEffect(() => { if (found && !found.bound) bindSwings(campaignId, found.index, first.group); }, [found?.bound, found?.index, first.group, campaignId]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = () => {
    if (!target || !swings || sending) return;
    setSending(true);
    let i = 0;
    const next = () => {
      const l = pending[i++];
      if (!l) { setSending(false); return; }
      // The swing as it was made, less the first target's concealment (the moment's, design/29 §2.6).
      const { concealment: _c, ...spec } = swings[l.swing] ?? swings[0]!;
      act({ kind: 'attack.request', attacker: l.token, target, attack: spec, private: isDm ? l.priv : false, leftover: l.id } as never, (reply) => {
        if (!reply.ok) { setSending(false); setNotice(reply.reason ?? 'That swing could not be sent.'); return; }
        markGone(campaignId, [l.id], 'sent');
        next();
      });
    };
    next();
  };
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 font-sans text-xs" data-testid={`leftover-${first.group}`}>
      <span className="text-amber-300">{attackerName}'s target is down. {n} {n === 1 ? 'swing' : 'swings'} left: send {n === 1 ? 'it' : 'them'} at…</span>
      {swings ? (
        <>
          <select aria-label="Send the swings left at" className="max-w-full rounded bg-stone-800 px-1 py-0.5 text-xs" value={target} onChange={(e) => setTarget(e.target.value)}>
            <option value="">{candidates.length ? 'Choose a creature' : melee && !isDm ? 'No creature within reach' : 'No creature to send them at'}</option>
            {candidates.map(({ o, rel }) => <option key={o.id} value={o.id}>{nameOf(o)}{rel === 'enemy' ? '' : ` (${rel})`}</option>)}
          </select>
          <button type="button" className={`${btn} border-amber-600`} disabled={!target || sending} onClick={send}>Send</button>
        </>
      ) : (
        <span className="text-stone-400">This full attack was made on another device: send them from there.</span>
      )}
      <button type="button" className={btn} disabled={sending} onClick={() => markGone(campaignId, pending.map((l) => l.id), 'let go')}>Let them go</button>
    </div>
  );
}

/** The parts of a player's own line that are a full attack's (qa-runs#1158). */
export function PlayerFullSwing({ attack }: { attack: PlayerAttack }) {
  if (!attack.full) return null;
  return <FullSwingParts id={attack.id} full={attack.full} leftover={!!attack.leftover} />;
}

/** The parts of the DM's record that are a full attack's (qa-runs#1158). */
export function DmFullSwing({ roll }: { roll: RollRecord }) {
  const f = roll.attack;
  if (!f?.full) return null;
  return <FullSwingParts id={roll.id} full={f.full} leftover={isLeftover(f)} fromSent={!!f.full.from} />;
}
